import { useCallback, useEffect, useState, type FormEvent } from "react";
import type {
  AdminContentSettingsResponse,
  AdminContentVideo,
  AdminContentVideoSlot,
  AdminContentVideoUsage,
  Locale,
  VideoKey
} from "../../shared/dto";
import { formatDate } from "./core";
import "./admin-settings-content.css";

/**
 * Servis wave 5: default content.
 *
 * The old panel was two url fields. It could tell you what was configured and
 * nothing about consequence: how many clients would see this video, how many
 * bring their own, how many already watched it. A global setting whose reach
 * is invisible is a setting nobody dares to change, so the overview leads and
 * the form follows, and every save answers with the object it created plus the
 * next sensible step.
 */

export function videoUsage(slot: AdminContentVideoSlot, hideTestTenants: boolean): AdminContentVideoUsage {
  return hideTestTenants ? slot.usage : slot.usageWithTest;
}

/**
 * F116: the market a slot serves, in the market's own name. The card exists
 * twice per video now, so the language is what tells the two apart - it leads
 * the heading rather than sitting in a note under the form.
 */
export function videoSlotLanguage(locale: Locale, t: (key: string) => string): string {
  return t(locale === "cs" ? "common.languageCs" : "common.languageEn");
}

export function videoDurationLabel(seconds: number | null, t: (key: string) => string): string {
  if (!seconds || seconds <= 0) return t("staffConsole.settingsContent.durationUnknown");
  if (seconds < 60) return t("staffConsole.settingsContent.durationSeconds").replace("{count}", String(Math.round(seconds)));
  return t("staffConsole.settingsContent.durationMinutes").replace("{count}", String(Math.round(seconds / 60)));
}

/** The reach sentence. Written as ONE sentence with three numbers because the
 * admin decides from the relation between them, not from three separate
 * counters. */
export function videoUsageSentence(usage: AdminContentVideoUsage, configured: boolean, t: (key: string) => string): string {
  const key = configured ? "staffConsole.settingsContent.usage" : "staffConsole.settingsContent.usageNoDefault";
  return t(key)
    .replace("{default}", String(Math.max(usage.usingDefault, 0)))
    .replace("{own}", String(usage.ownVideo))
    .replace("{done}", String(usage.completed))
    .replace("{clients}", String(usage.clients));
}

export function AdminSettingsContent(props: {
  csrfToken: string;
  locale: Locale;
  hideTestTenants: boolean;
  t: (key: string) => string;
}) {
  const { t } = props;
  const [videos, setVideos] = useState<AdminContentVideo[] | null>(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const response = await fetch("/api/admin/settings/content", { credentials: "include" });
      if (!response.ok) throw new Error("settings_content_failed");
      const body = await response.json() as AdminContentSettingsResponse;
      setVideos(body.videos);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    // S2-12: one name for the panel, in its summary. The body used to open
    // with "Výchozí obsah" under a summary that said "Výchozí videa".
    <section className="settings-content" aria-label={t("staffConsole.videos.defaultsTitle")}>
      {failed && (
        <div className="settings-content-error">
          <p className="message error-message" role="alert">{t("staffConsole.settingsContent.loadError")}</p>
          <button className="secondary-button compact-button" type="button" onClick={() => void load()}>{t("common.retry")}</button>
        </div>
      )}
      {!failed && videos === null && <p className="staff-empty">{t("common.loading")}</p>}
      {/* F116: one card per market per video. Servis holds the Czech and the
          American recording of the same step side by side, because a client
          gated behind a video in a language they do not read cannot move. */}
      {videos?.flatMap((video) => video.slots.map((slot) => (
        <VideoDefaultCard key={`${video.key}-${slot.locale}`} videoKey={video.key} slot={slot}
          csrfToken={props.csrfToken} locale={props.locale}
          hideTestTenants={props.hideTestTenants} t={t} onSaved={load} />
      )))}
    </section>
  );
}

type SaveState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved"; title: string; reach: number }
  | { kind: "cleared" }
  | { kind: "invalid_unlock" }
  | { kind: "error" };

/** F81: the unlock moment must sit inside the video, which means the admin
 * has to tell us how long the video is. Mirrors the server's
 * validVideoConfigValue rule so the form can refuse before the request. */
export function unlockAtIssue(unlockAtSeconds: number | undefined, durationSeconds: number | undefined): boolean {
  if (unlockAtSeconds === undefined) return false;
  if (!Number.isFinite(unlockAtSeconds) || unlockAtSeconds <= 0) return true;
  return durationSeconds === undefined || unlockAtSeconds > durationSeconds;
}

function VideoDefaultCard(props: {
  videoKey: VideoKey;
  slot: AdminContentVideoSlot;
  csrfToken: string;
  locale: Locale;
  hideTestTenants: boolean;
  t: (key: string) => string;
  onSaved: () => Promise<void>;
}) {
  const { slot, t } = props;
  const video = { key: props.videoKey, configured: slot.configured };
  const usage = videoUsage(slot, props.hideTestTenants);
  const [url, setUrl] = useState(video.configured?.url ?? "");
  const [title, setTitle] = useState(video.configured?.title ?? "");
  const [duration, setDuration] = useState(video.configured?.durationSeconds ? String(video.configured.durationSeconds) : "");
  const [unlockAt, setUnlockAt] = useState(video.configured?.unlockAtSeconds ? String(video.configured.unlockAtSeconds) : "");
  const [state, setState] = useState<SaveState>({ kind: "idle" });

  async function save(event: FormEvent) {
    event.preventDefault();
    setState({ kind: "saving" });
    const durationSeconds = duration.trim() ? Number(duration.trim()) : undefined;
    const unlockAtSeconds = unlockAt.trim() ? Number(unlockAt.trim()) : undefined;
    // F81: refuse an unlock moment outside the video (or without a duration)
    // BEFORE the request; the server enforces the same rule with a 400.
    if (unlockAtIssue(unlockAtSeconds, durationSeconds)) {
      setState({ kind: "invalid_unlock" });
      return;
    }
    const value: Record<string, unknown> = { url: url.trim(), title: title.trim() };
    if (durationSeconds !== undefined && Number.isFinite(durationSeconds) && durationSeconds > 0) {
      value.duration_seconds = durationSeconds;
    }
    if (unlockAtSeconds !== undefined) {
      value.unlock_at_seconds = unlockAtSeconds;
    }
    try {
      const response = await fetch(`/api/admin/video-defaults/${video.key}/${slot.locale}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": props.csrfToken },
        body: JSON.stringify({ value })
      });
      if (!response.ok) throw new Error("video_default_save_failed");
      // The reach BEFORE the reload is already the right number: a new default
      // reaches exactly the clients without their own video.
      setState({ kind: "saved", title: value.title as string, reach: Math.max(usage.clients - usage.ownVideo, 0) });
      await props.onSaved();
    } catch {
      setState({ kind: "error" });
    }
  }

  async function clear() {
    if (!window.confirm(t("staffConsole.videos.confirmClearDefault"))) return;
    setState({ kind: "saving" });
    try {
      const response = await fetch(`/api/admin/video-defaults/${video.key}/${slot.locale}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "X-Launchpad-CSRF": props.csrfToken }
      });
      if (!response.ok) throw new Error("video_default_clear_failed");
      setUrl("");
      setTitle("");
      setDuration("");
      setUnlockAt("");
      setState({ kind: "cleared" });
      await props.onSaved();
    } catch {
      setState({ kind: "error" });
    }
  }

  return (
    <article className="staff-card settings-video-card" data-video-default={`${video.key}:${slot.locale}`}>
      <div className="settings-video-head">
        <h3>{`${t(`staffConsole.videos.kind.${video.key}`)} · ${videoSlotLanguage(slot.locale, t)}`}</h3>
        <span className={video.configured ? "status-chip on" : "status-chip off"}>
          {t(video.configured ? "staffConsole.settingsContent.configured" : "staffConsole.settingsContent.notConfigured")}
        </span>
      </div>
      <p className="settings-video-where">{t(`staffConsole.settingsContent.where.${video.key}`)}</p>
      <p className="settings-video-usage">{videoUsageSentence(usage, Boolean(video.configured), t)}</p>
      {/* F116: the Czech slot serves the pre-F116 shared row until somebody
          saves it once. Saying so is the difference between a slot that is
          filled in and one that merely inherits. */}
      {video.configured?.legacy && (
        <p className="admin-tool-hint">{t("staffConsole.settingsContent.legacyDefault")}</p>
      )}
      {video.configured && (
        <dl className="settings-video-facts">
          <dt>{t("staffConsole.videos.titleLabel")}</dt>
          <dd>{video.configured.title}</dd>
          <dt>{t("staffConsole.videos.urlLabel")}</dt>
          <dd><a href={video.configured.url} rel="noreferrer" target="_blank">{video.configured.url}</a></dd>
          <dt>{t("staffConsole.videos.durationLabel")}</dt>
          <dd>{videoDurationLabel(video.configured.durationSeconds, t)}</dd>
          <dt>{t("staffConsole.videos.unlockAtLabel")}</dt>
          <dd>{video.configured.unlockAtSeconds
            ? `${Math.round(video.configured.unlockAtSeconds)} s`
            : t("staffConsole.videos.unlockAtHint")}</dd>
          <dt>{t("staffConsole.settingsContent.updatedAt")}</dt>
          <dd>{video.configured.updatedAt ? formatDate(video.configured.updatedAt, props.locale) : t("staffConsole.settingsContent.durationUnknown")}</dd>
        </dl>
      )}
      <form className="editor-form" onSubmit={save}>
        <label className="field-label" htmlFor={`video-url-${video.key}-${slot.locale}`}>{t("staffConsole.videos.urlLabel")}</label>
        <input id={`video-url-${video.key}-${slot.locale}`} type="url" value={url} required
          placeholder="https://" onChange={(event) => { setUrl(event.target.value); setState({ kind: "idle" }); }} />
        <label className="field-label" htmlFor={`video-title-${video.key}-${slot.locale}`}>{t("staffConsole.videos.titleLabel")}</label>
        <input id={`video-title-${video.key}-${slot.locale}`} type="text" value={title} required maxLength={200}
          onChange={(event) => { setTitle(event.target.value); setState({ kind: "idle" }); }} />
        <label className="field-label" htmlFor={`video-duration-${video.key}-${slot.locale}`}>{t("staffConsole.videos.durationLabel")}</label>
        <input id={`video-duration-${video.key}-${slot.locale}`} type="number" min={1} step={1} value={duration}
          onChange={(event) => { setDuration(event.target.value); setState({ kind: "idle" }); }} />
        <label className="field-label" htmlFor={`video-unlock-${video.key}-${slot.locale}`}>{t("staffConsole.videos.unlockAtLabel")}</label>
        <input id={`video-unlock-${video.key}-${slot.locale}`} type="number" min={1} step={1} value={unlockAt}
          onChange={(event) => { setUnlockAt(event.target.value); setState({ kind: "idle" }); }} />
        <p className="admin-tool-hint">{t("staffConsole.videos.unlockAtHint")}</p>
        <div className="button-row">
          <button className="primary-button" type="submit" disabled={state.kind === "saving" || !url.trim() || !title.trim()}>
            {state.kind === "saving" ? t("staffConsole.settingsContent.saving") : t("staffConsole.save")}
          </button>
          {video.configured && (
            <button className="text-button danger" type="button" disabled={state.kind === "saving"} onClick={() => void clear()}>
              {t("staffConsole.delete")}
            </button>
          )}
        </div>
        {/* S2-19: a closed control names its condition next to itself. */}
        {state.kind !== "saving" && (!url.trim() || !title.trim()) && (
          <p className="admin-tool-hint">{t("staffConsole.settingsContent.saveNeeds")}</p>
        )}
        <VideoSaveResult state={state} videoKey={video.key} t={t} onRetry={() => setState({ kind: "idle" })} />
      </form>
    </article>
  );
}

/** Zákon akční zpětné vazby: ready, working, done, error, safe repeat. "Done"
 * names the video, says who will see it and offers the next step. */
export function VideoSaveResult(props: {
  state: SaveState;
  videoKey: string;
  t: (key: string) => string;
  onRetry: () => void;
}) {
  const { state, t } = props;
  if (state.kind === "saved") {
    return (
      <div className="message success-message settings-video-result" role="status">
        <strong>
          {t("staffConsole.settingsContent.savedTitle")
            .replace("{video}", t(`staffConsole.videos.kind.${props.videoKey}`))
            .replace("{title}", state.title)}
        </strong>
        <span>{t("staffConsole.settingsContent.savedReach").replace("{count}", String(state.reach))}</span>
        <span>{t("staffConsole.settingsContent.savedNext")}</span>
      </div>
    );
  }
  if (state.kind === "cleared") {
    return (
      <div className="message success-message settings-video-result" role="status">
        <strong>{t("staffConsole.settingsContent.clearedTitle")}</strong>
        <span>{t("staffConsole.settingsContent.clearedNext")}</span>
      </div>
    );
  }
  if (state.kind === "invalid_unlock") {
    return (
      <div className="message error-message settings-video-result" role="alert">
        <strong>{t("staffConsole.settingsContent.unlockAtInvalid")}</strong>
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <div className="message error-message settings-video-result" role="alert">
        <strong>{t("staffConsole.settingsContent.saveError")}</strong>
        <span>{t("staffConsole.settingsContent.saveErrorHint")}</span>
        <button className="secondary-button compact-button" type="button" onClick={props.onRetry}>
          {t("common.retry")}
        </button>
      </div>
    );
  }
  return null;
}
